import React, { useEffect, useState } from 'react';
import { ArrowLeft, Copy, AlertTriangle, RefreshCw, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * USDT 입금 화면 (BNB 체인 / BEP-20) — 2026-09-13 신규
 * 2026-09-14: 입금 네트워크 선택(BSC/트론) 추가.
 *
 * 회원마다 전용 입금주소를 하나씩 발급한다. 주소가 곧 신원이라
 * 누가 보냈는지 서버가 자동으로 안다(금액 꼬리 같은 편법 불필요).
 *
 * 화면에서 제일 중요한 건 "네트워크 경고"다. USDT는 이더리움·트론·BSC 등
 * 여러 체인에 같은 이름으로 존재하는데, 다른 네트워크로 보내면 사실상 복구가
 * 불가능하다. 그래서 경고를 주소보다 먼저, 크게 띄운다.
 *
 * [중요] 탭은 **서버가 쓸 수 있다고 알려준 체인이 2개 이상일 때만** 그린다.
 * 트론 xpub 이 준비되기 전에 탭을 보여주면 회원이 눌렀을 때 에러를 본다.
 * 쓸 수 있는 체인이 하나면 화면은 이전과 완전히 동일하다.
 */

interface Props {
  onBack: () => void;
}

interface DepositRow {
  network?: string;
  tx_hash: string;
  amount_usdt: string;
  status: string;
  credited_at: string | null;
  detected_at: string;
}

type Net = 'bsc' | 'tron';

const NETWORKS: Record<Net, { label: string; badge: string; badgeBg: string; badgeFg: string; warn: string; other: string }> = {
  bsc: {
    label: 'BNB 체인',
    badge: 'BEP-20',
    badgeBg: '#FEF3C7',
    badgeFg: '#B45309',
    warn: '반드시 BNB 스마트체인(BEP-20)으로 보내세요',
    other: '이더리움(ERC-20), 트론(TRC-20) 등',
  },
  tron: {
    label: '트론',
    badge: 'TRC-20',
    badgeBg: '#FEE2E2',
    badgeFg: '#B91C1C',
    warn: '반드시 트론(TRC-20)으로 보내세요',
    other: '이더리움(ERC-20), BNB 스마트체인(BEP-20) 등',
  },
};

const STATUS_LABEL: Record<string, { text: string; color: string; bg: string }> = {
  detected: { text: '확인 중', color: '#B45309', bg: '#FEF3C7' },
  credited: { text: '입금 완료', color: '#15803D', bg: '#DCFCE7' },
  failed:   { text: '처리 실패', color: '#B91C1C', bg: '#FEE2E2' },
  ignored:  { text: '보류', color: '#6B7280', bg: '#F3F4F6' },
};

export function UsdtDeposit({ onBack }: Props) {
  const [nets, setNets] = useState<Net[]>(['bsc']);
  const [net, setNet] = useState<Net>('bsc');
  const [address, setAddress] = useState<string>('');
  const [qr, setQr] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [deposits, setDeposits] = useState<DepositRow[]>([]);

  const cfg = NETWORKS[net];

  const authHeader = () => {
    const t = localStorage.getItem('handy-jwt-token') || '';
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadNetworks = async () => {
    try {
      const r = await fetch('/api/bsc/networks');
      const d = await r.json();
      const list = (d.networks || []).map((n: any) => n.network).filter((n: string) => n in NETWORKS);
      if (list.length > 0) setNets(list);
    } catch { /* 실패하면 기존대로 BSC만 */ }
  };

  const loadAddress = async (n: Net) => {
    setLoading(true);
    setError('');
    setAddress('');
    setQr('');
    try {
      const r = await fetch(`/api/bsc/my-address?network=${n}`, { headers: authHeader() as any });
      const d = await r.json();
      if (!r.ok || !d.address) throw new Error(d.error || '입금주소를 받아오지 못했습니다');
      setAddress(d.address);
      const QRCode = await import('qrcode');
      setQr(await QRCode.toDataURL(d.address, { width: 320, margin: 1 }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDeposits = async () => {
    try {
      const r = await fetch('/api/bsc/my-deposits', { headers: authHeader() as any });
      const d = await r.json();
      if (d.success) setDeposits(d.deposits || []);
    } catch { /* 내역 조회 실패는 화면을 막지 않는다 */ }
  };

  useEffect(() => { loadNetworks(); }, []);
  useEffect(() => { loadAddress(net); }, [net]);

  useEffect(() => {
    loadDeposits();
    // 입금은 15초쯤 뒤에 반영된다. 화면을 열어둔 동안 주기적으로 새로고침한다.
    const t = setInterval(loadDeposits, 15000);
    return () => clearInterval(t);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('입금주소를 복사했습니다.');
    } catch {
      const el = document.createElement('textarea');
      el.value = address;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success('입금주소를 복사했습니다.');
    }
  };

  // 내역은 선택한 체인 것만 보여준다. network 가 없는 옛 기록은 BSC 로 본다.
  const shown = nets.length > 1
    ? deposits.filter(d => (d.network || 'bsc') === net)
    : deposits;

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center gap-3 px-4 py-4">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center rounded-xl bg-muted">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-foreground">USDT 입금</h1>
      </div>

      <div className="px-4 pb-24 space-y-4">
        {/* 네트워크 선택 — 쓸 수 있는 체인이 2개 이상일 때만 */}
        {nets.length > 1 && (
          <div className="flex gap-1 p-1 rounded-2xl" style={{ background: '#F3F4F6', border: '1px solid #E5E7EB' }}>
            {nets.map((n) => {
              const c = NETWORKS[n];
              const on = n === net;
              return (
                <button
                  key={n}
                  onClick={() => setNet(n)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                  style={{
                    background: on ? '#FFFFFF' : 'transparent',
                    color: on ? '#111827' : '#9CA3AF',
                    boxShadow: on ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  {c.label}
                  <span className="ml-1.5 text-[10px] font-bold" style={{ color: on ? c.badgeFg : '#C4C7CC' }}>
                    {c.badge}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* 네트워크 경고 — 주소보다 먼저 보여준다 */}
        <div className="rounded-2xl p-4" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
          <div className="flex gap-2.5">
            <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: '#DC2626' }} />
            <div>
              <p className="text-sm font-bold" style={{ color: '#B91C1C' }}>
                {cfg.warn}
              </p>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: '#7F1D1D' }}>
                {cfg.other} <b>다른 네트워크로 보내면 자산을 되찾을 수 없습니다.</b>
                <br />
                이 주소는 <b>USDT 전용</b>입니다. 다른 코인을 보내지 마세요.
              </p>
            </div>
          </div>
        </div>

        {/* 입금주소 */}
        <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-foreground">내 전용 입금주소</span>
            <span
              className="text-[11px] px-2 py-1 rounded-full font-bold"
              style={{ background: cfg.badgeBg, color: cfg.badgeFg }}
            >
              {cfg.badge}
            </span>
          </div>

          {loading ? (
            <p className="text-sm text-center py-8" style={{ color: '#9CA3AF' }}>주소를 불러오는 중…</p>
          ) : error ? (
            <div className="text-center py-6">
              <p className="text-sm" style={{ color: '#B91C1C' }}>{error}</p>
              <button
                onClick={() => loadAddress(net)}
                className="mt-3 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: '#FF6B00', color: '#FFFFFF' }}
              >
                다시 시도
              </button>
            </div>
          ) : (
            <>
              {qr && (
                <div className="flex justify-center mb-4">
                  <img src={qr} alt="입금주소 QR" style={{ width: 180, height: 180, borderRadius: 12 }} />
                </div>
              )}
              <div
                className="rounded-xl p-3 text-sm font-mono break-all"
                style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', color: '#111827' }}
              >
                {address}
              </div>
              <button
                onClick={copy}
                className="mt-3 w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2"
                style={{ background: '#FF6B00', color: '#FFFFFF' }}
              >
                <Copy className="w-4 h-4" />
                주소 복사하기
              </button>
              <p className="text-[11px] mt-2.5 text-center" style={{ color: '#6B7280' }}>
                이 주소는 회원님 전용입니다. 입금되면 약 30초 안에 자동 반영됩니다.
              </p>
            </>
          )}
        </div>

        {/* 입금 내역 */}
        <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-foreground">입금 내역</span>
            <button onClick={loadDeposits} className="p-1.5 rounded-lg" style={{ background: '#F3F4F6' }}>
              <RefreshCw className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
            </button>
          </div>

          {shown.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: '#9CA3AF' }}>
              아직 입금 내역이 없습니다.
            </p>
          ) : (
            <div className="space-y-2">
              {shown.map((d) => {
                const s = STATUS_LABEL[d.status] || STATUS_LABEL.ignored;
                return (
                  <div
                    key={d.tx_hash}
                    className="flex items-center justify-between rounded-xl p-3"
                    style={{ background: '#F9FAFB' }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground">
                        {Number(d.amount_usdt).toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT
                      </p>
                      <p className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>
                        {new Date(d.credited_at || d.detected_at).toLocaleString('ko-KR')}
                      </p>
                    </div>
                    <span
                      className="text-[11px] px-2.5 py-1 rounded-full font-bold flex items-center gap-1 shrink-0"
                      style={{ background: s.bg, color: s.color }}
                    >
                      {d.status === 'credited' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {s.text}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
